import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { TransportOrder } from '../models/transport-order.model';

export type EntityResponseType = HttpResponse<TransportOrder>;
export type EntityArrayResponseType = HttpResponse<TransportOrder[]>;

@Injectable({ providedIn: 'root' })
export class TransportOrderService {
  protected resourceUrl: string;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/transport-orders');
  }

  create(transportOrder: TransportOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.http.post<TransportOrder>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(transportOrder: TransportOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.http
      .put<TransportOrder>(`${this.resourceUrl}/${transportOrder.id}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(transportOrder: TransportOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.http
      .patch<TransportOrder>(`${this.resourceUrl}/${transportOrder.id}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<TransportOrder> {
    return this.http
      .get<TransportOrder>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(mapAsBody)
      .pipe(map((res: any) => new TransportOrder(res)));
  }

  public query(req?: any): Observable<HttpResponse<TransportOrder[]>> {
    const options = createRequestOption(req);
    return this.http.get<TransportOrder[]>(this.resourceUrl, { params: options, observe: 'response' }).pipe(
      map((res: HttpResponse<TransportOrder[]>) => {
        return new HttpResponse<TransportOrder[]>({
          body: (res.body || []).map(v => new TransportOrder(v)),
          url: res.url as string,
          headers: res.headers,
          status: res.status,
        });
      })
    );
  }

  public delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addTransportOrderToCollectionIfMissing(transportOrderCollection: TransportOrder[], ...transportOrdersToCheck: (TransportOrder | null | undefined)[]): TransportOrder[] {
    const transportOrders: TransportOrder[] = transportOrdersToCheck.filter(isPresent);
    if (transportOrders.length > 0) {
      const transportOrderCollectionIdentifiers = transportOrderCollection.map(transportOrder => transportOrder.id);
      const transportOrdersToAdd = transportOrders.filter(transportOrder => {
        const transportOrderIdentifier = transportOrder.id;
        if (transportOrderIdentifier == null || transportOrderCollectionIdentifiers.includes(transportOrderIdentifier)) {
          return false;
        }
        transportOrderCollectionIdentifiers.push(transportOrderIdentifier);
        return true;
      });
      return [...transportOrdersToAdd, ...transportOrderCollection];
    }
    return transportOrderCollection;
  }

  protected convertDateFromClient(transportOrder: TransportOrder): TransportOrder {
    return Object.assign({}, transportOrder, {
      pickupDate: transportOrder.pickupDate?.isValid() ? transportOrder.pickupDate.format(DATE_FORMAT) : undefined,
      deliveryDate: transportOrder.deliveryDate?.isValid() ? transportOrder.deliveryDate.format(DATE_FORMAT) : undefined,
      creationDate: transportOrder.creationDate?.isValid() ? transportOrder.creationDate.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.pickupDate = res.body.pickupDate ? dayjs(res.body.pickupDate) : undefined;
      res.body.deliveryDate = res.body.deliveryDate ? dayjs(res.body.deliveryDate) : undefined;
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((transportOrder: TransportOrder) => {
        transportOrder.pickupDate = transportOrder.pickupDate ? dayjs(transportOrder.pickupDate) : undefined;
        transportOrder.deliveryDate = transportOrder.deliveryDate ? dayjs(transportOrder.deliveryDate) : undefined;
        transportOrder.creationDate = transportOrder.creationDate ? dayjs(transportOrder.creationDate) : undefined;
      });
    }
    return res;
  }
}
